/**
 * LARCIN — Fixed Bundle Builder (assets/larcin-fixed-bundle.js)
 *
 * WHAT GOES IN THE CART
 * ---------------------
 * Exactly one line: the parent bundle product's variant, quantity 1, at
 * whatever price Shopify has on that variant. The seven component picks
 * are attached to that line as properties. Nothing here ever adds a
 * component variant to the cart, so the fixed price cannot be gamed from
 * the client and no component can leak into the cart as its own line.
 *
 * Two properties are customer-visible (they render under the line in the
 * cart, at checkout, in the confirmation email and on the packing slip):
 *
 *   Full-size (50ml) : Vice City, Vermilion, Old Money
 *   Samples (5ml)    : Brown Sugar, Aristocrat, Pacifica, Heaven's Cut
 *
 * The rest are underscore-prefixed, which hides them from the customer
 * while leaving them readable on the order in the Shopify admin and to
 * Shopify Flow. Shopify's ADMIN order page shows underscore properties in
 * full and gives no way to hide them, so these are kept deliberately
 * short — the only lever on admin clutter is value size:
 *
 *   _bundle   "larcin-3x50-4x5 / LFB-XXXXX" — type and unique id
 *   _inv      "41586104598727x1|41586104598728x1|..." — inventory item
 *             ids with quantity. The ONLY property Flow reads.
 *   _edit     rebuild link with plain variant ids
 *
 * _inv carries INVENTORY ITEM ids, not variant ids. Those two are
 * different, and inventoryAdjustQuantities needs the former. Liquid can't
 * derive it, and Flow's Send Admin API request supports mutations only —
 * no queries — so it cannot look it up at order time either. The ids are
 * therefore pre-loaded into the variant metafield
 * custom.inventory_item_id by a one-time GraphQL job, read by the card
 * snippet, and stamped onto the order here. Flow then does zero lookups.
 *
 * VALIDATION
 * ----------
 * Stock is per inventory item, and Shopify does NOT check a bundle's
 * components at checkout — the line is the bundle product, and Flow takes
 * the components out of stock only after the order. So the limit is
 * enforced here: each card's stock (data-cap) less what the cart already
 * holds of that item, counting every other bundle line's _inv and every
 * plain line of the same variant. The cart is read on load and after every
 * cart change, so the + button, the toast and the final check all use the
 * same number.
 *
 * The live product JSON is also checked (prefetched as soon as the bundle
 * is complete, so the click doesn't wait on it): a fragrance that sold out
 * mid-session blocks the add and is flagged on its card rather than
 * silently shipping short.
 *
 * PERSISTENCE
 * -----------
 * Selections survive a reload and a return visit via sessionStorage, so
 * "go back and change your picks" costs the customer nothing.
 */

import { CartLinesUpdateEvent } from '@shopify/events';

const STAGES = ['full', 'sample'];

class LarcinFixedBundle extends HTMLElement {
  /** @type {{full: Map<string, number>, sample: Map<string, number>}} */
  #sel = { full: new Map(), sample: new Map() };
  /** @type {Map<string, HTMLElement>} */
  #cards = new Map();
  #config = null;
  #fmt = null;
  #stage = 'full';
  #submitting = false;
  #query = '';
  #family = '';
  /** @type {WeakMap<HTMLElement, number>} */
  #tapTimers = new WeakMap();
  #handOffTimer = null;
  #toastTimer = null;
  #observer = null;
  /** Inventory key → units the cart already holds (see #applyCart). */
  #reserved = new Map();
  #reservedAt = 0;
  #reservedSeq = 0;
  #reservedPromise = null;
  #cartTimer = null;
  /** Variant id → inventory key, for plain cart lines. */
  #keyByVariant = new Map();
  /** Handle → { at, promise } of its live product JSON. */
  #live = new Map();
  #onCartChange = () => {
    clearTimeout(this.#cartTimer);
    this.#cartTimer = setTimeout(() => this.#refreshReserved(), 250);
  };
  #onPageShow = (event) => {
    if (event.persisted) this.#refreshReserved();
  };

  connectedCallback() {
    const raw = this.querySelector('[data-lfb-config]')?.textContent;
    try {
      this.#config = JSON.parse(raw || '{}');
    } catch {
      console.error('[larcin-fixed-bundle] config parse failed');
      return;
    }

    this.#fmt = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
      style: 'currency',
      currency: this.#config.currency || 'AUD',
    });

    this.#indexCards();
    this.#bind();
    this.#restore();
    this.#watchScroll();
    this.#render();

    // What the cart already holds counts against each fragrance's stock.
    this.#refreshReserved();
    document.addEventListener('shopify:cart:lines-update', this.#onCartChange);
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    clearTimeout(this.#toastTimer);
    clearTimeout(this.#cartTimer);
    document.removeEventListener('shopify:cart:lines-update', this.#onCartChange);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /* ================= setup ================= */

  #indexCards() {
    for (const card of this.querySelectorAll('[data-lfb-card]')) {
      this.#cards.set(`${card.dataset.stage}:${card.dataset.variantId}`, card);
      this.#keyByVariant.set(String(card.dataset.variantId), this.#keyOf(card));
    }
  }

  /* ================= stock ================= */

  /** Stock is held per inventory item; the variant id stands in when a card has none. */
  #keyOf(card) {
    return (card.dataset.invId || '').trim() || `v${card.dataset.variantId}`;
  }

  /** Units of this card's stock that can still go into this bundle. */
  #capOf(card) {
    const stock = Number(card.dataset.cap || 99);
    return Math.max(0, stock - (this.#reserved.get(this.#keyOf(card)) || 0));
  }

  /** Units of this card's inventory item picked in the current bundle, across both stages. */
  #pickedOf(card) {
    const key = this.#keyOf(card);
    let n = 0;
    for (const stage of STAGES) {
      for (const [id, qty] of this.#sel[stage]) {
        const other = this.#card(stage, id);
        if (other && this.#keyOf(other) === key) n += qty;
      }
    }
    return n;
  }

  #stockMessage(card) {
    const stock = Number(card.dataset.cap || 99);
    const inCart = this.#reserved.get(this.#keyOf(card)) || 0;
    const name = [card.dataset.title, card.dataset.size].filter(Boolean).join(' ');
    if (inCart > 0) {
      const left = Math.max(0, stock - inCart);
      if (left === 0) return `All ${stock} ${name} in stock ${stock === 1 ? 'is' : 'are'} already in your cart.`;
      return `Only ${stock} ${name} left in stock and ${inCart} ${inCart === 1 ? 'is' : 'are'} already in your cart, so this bundle can include ${left}.`;
    }
    return `Only ${stock} of ${name} left in stock.`;
  }

  /**
   * Re-reads the cart. Requests are numbered so an older answer that lands
   * after a newer one is dropped instead of painting stale counts.
   */
  #refreshReserved() {
    const seq = ++this.#reservedSeq;
    const promise = this.#fetchCart()
      .then((cart) => {
        if (seq === this.#reservedSeq) this.#applyCart(cart);
      })
      .catch(() => {
        /* Unknown is treated as nothing reserved; the card's own stock still applies. */
      })
      .finally(() => {
        if (this.#reservedPromise === promise) this.#reservedPromise = null;
      });
    this.#reservedPromise = promise;
    return promise;
  }

  /** Recent enough is fine: every cart change in this tab refreshes it anyway. */
  async #ensureReserved() {
    if (this.#reservedAt && Date.now() - this.#reservedAt < 30000 && !this.#reservedPromise) return;
    await (this.#reservedPromise || this.#refreshReserved());
  }

  /**
   * Units already spoken for, by inventory item: every bundle line's _inv
   * ("<inventory item id>x<qty>|…") times the line's quantity, plus every
   * plain line of a variant on these cards.
   */
  #applyCart(cart) {
    const reserved = new Map();
    const add = (key, n) => reserved.set(key, (reserved.get(key) || 0) + n);
    for (const item of cart?.items || []) {
      const qty = Number(item.quantity) || 0;
      const inv = item.properties && item.properties._inv;
      if (inv) {
        for (const part of String(inv).split('|')) {
          const match = /^(\d+)x(\d+)$/.exec(part.trim());
          if (match) add(match[1], Number(match[2]) * qty);
        }
        continue;
      }
      const key = this.#keyByVariant.get(String(item.variant_id));
      if (key) add(key, qty);
    }
    this.#reserved = reserved;
    this.#reservedAt = Date.now();
    this.#render();
  }

  /** The live product JSON for a handle, reused for a minute. */
  #liveFor(handle) {
    const cached = this.#live.get(handle);
    if (cached && Date.now() - cached.at < 60000) return cached.promise;
    const promise = fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null);
    this.#live.set(handle, { at: Date.now(), promise });
    return promise;
  }

  #selectedHandles() {
    const handles = new Set();
    for (const stage of STAGES) {
      for (const id of this.#sel[stage].keys()) {
        const card = this.#card(stage, id);
        if (card) handles.add(card.dataset.handle);
      }
    }
    return [...handles];
  }

  #card(stage, variantId) {
    return this.#cards.get(`${stage}:${variantId}`) || null;
  }

  #limit(stage) {
    return stage === 'full' ? Number(this.#config.fullCount) : Number(this.#config.sampleCount);
  }

  #chosen(stage) {
    let n = 0;
    for (const q of this.#sel[stage].values()) n += q;
    return n;
  }

  #remaining(stage) {
    return Math.max(0, this.#limit(stage) - this.#chosen(stage));
  }

  #isComplete() {
    return STAGES.every((s) => this.#remaining(s) === 0);
  }

  /* ================= events ================= */

  #bind() {
    // Stage tabs
    for (const tab of this.querySelectorAll('[data-lfb-stage-tab]')) {
      tab.addEventListener('click', () => this.#setStage(tab.dataset.lfbStageTab, true));
    }

    // Card interactions. Delegated so both grids share one listener and
    // nothing needs rebinding when cards are filtered.
    this.addEventListener('click', (event) => {
      const card = event.target.closest?.('[data-lfb-card]');
      if (!card || !this.contains(card)) return;

      if (event.target.closest('[data-lfb-dec]')) {
        event.preventDefault();
        this.#change(card, -1);
        return;
      }
      if (event.target.closest('[data-lfb-inc]') || event.target.closest('[data-lfb-select]')) {
        event.preventDefault();
        this.#change(card, +1);
        return;
      }
      // Tapping the body adds, whether or not the fragrance is already in
      // the bundle. #change still refuses when the stage is full, the
      // fragrance is sold out, or duplicates are off.
      this.#change(card, +1);
    });

    // Keyboard: cards are list items, so give the select button the work.
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const slotX = event.target.closest?.('[data-lfb-slot-remove]');
      if (slotX) {
        event.preventDefault();
        slotX.click();
      }
    });

    // Slot removal
    this.addEventListener('click', (event) => {
      const btn = event.target.closest?.('[data-lfb-slot-remove]');
      if (!btn || !this.contains(btn)) return;
      event.preventDefault();
      const { stage, variantId } = btn.dataset;
      const card = this.#card(stage, variantId);
      if (card) this.#change(card, -1);
    });

    // Search
    const search = this.querySelector('[data-lfb-search]');
    let debounce;
    search?.addEventListener('input', (event) => {
      clearTimeout(debounce);
      const value = event.target.value;
      debounce = setTimeout(() => {
        this.#query = this.#normalize(value);
        this.#filter();
      }, 140);
    });

    // Mobile sheet
    const sheet = this.querySelector('[data-lfb-sheet]');
    for (const opener of this.querySelectorAll('[data-lfb-open-sheet]')) {
      opener.addEventListener('click', () => sheet?.showModal());
    }
    this.querySelector('[data-lfb-close-sheet]')?.addEventListener('click', () => sheet?.close());
    sheet?.addEventListener('click', (event) => {
      if (event.target === sheet) sheet.close();
    });

    // Scent-family chips. They run through the same filter as the search,
    // so one thing decides what a customer can see.
    for (const chip of this.querySelectorAll('[data-lfb-chip]')) {
      chip.addEventListener('click', () => {
        const value = chip.dataset.lfbChip || '';
        this.#family = this.#family === value ? '' : value;
        for (const other of this.querySelectorAll('[data-lfb-chip]')) {
          const on = (other.dataset.lfbChip || '') === this.#family;
          other.classList.toggle('is-on', on);
          other.setAttribute('aria-pressed', on ? 'true' : 'false');
        }
        this.#filter();
      });
    }

    // Add to cart — every copy of the button drives the same handler.
    for (const button of this.querySelectorAll('[data-lfb-atc]')) {
      button.addEventListener('click', () => this.#addToCart());
    }
  }

  #watchScroll() {
    const hero = this.querySelector('.lfb-hero');
    const bar = this.querySelector('[data-lfb-bar]');
    if (!hero || !bar) return;

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        const show = !entry.isIntersecting;
        bar.classList.toggle('is-visible', show);
        this.toggleAttribute('data-bar-visible', show);
      },
      { rootMargin: '0px 0px -40% 0px' }
    );
    this.#observer.observe(hero);
  }

  /* ================= selection ================= */

  #change(card, delta) {
    if (card.dataset.soldout === 'true') {
      this.#toast(this.#config.text.soldOut, true);
      return;
    }

    const stage = card.dataset.stage;
    const id = card.dataset.variantId;
    const map = this.#sel[stage];
    const current = map.get(id) || 0;

    if (delta > 0) {
      if (this.#remaining(stage) === 0) {
        this.#toast(this.#config.text.stageFull, true);
        return;
      }
      if (current > 0 && !this.#config.allowDuplicates) {
        this.#toast('You’ve already picked that one.', true);
        return;
      }
      // Stock left after what the cart already holds, against everything of
      // this fragrance already in the bundle.
      if (this.#pickedOf(card) + 1 > this.#capOf(card)) {
        this.#toast(this.#stockMessage(card), true);
        return;
      }
      map.set(id, current + 1);
    } else {
      if (current <= 1) map.delete(id);
      else map.set(id, current - 1);
    }

    this.#pulse(card);
    this.#persist();
    this.#render();

    // A finished stage hands over to the one that still needs picks, in
    // whichever direction the customer happens to be building. The tabs stay
    // live throughout, so this is a shortcut and never a lock.
    if (delta > 0) this.#handOff(stage);
  }

  /**
   * Called after a pick lands. If that pick finished its stage and the other
   * stage is still short, say so and move across.
   *
   * The pause is there so the card the customer just tapped has time to show
   * itself as chosen; switching on the same frame reads as the tap having
   * gone somewhere else.
   */
  #handOff(from) {
    if (this.#remaining(from) !== 0) return;

    const to = STAGES.find((stage) => stage !== from);
    if (!to || this.#remaining(to) === 0) return;

    // A merchant who clears the line gets the move without the toast.
    const line = (this.#config.text.handoff || {})[from];
    if (line) this.#toast(line);

    clearTimeout(this.#handOffTimer);
    this.#handOffTimer = setTimeout(() => this.#setStage(to, true), 450);
  }

  /**
   * Confirms the tap landed. The card presses in for a quarter of a second,
   * and on a device with the Vibration API the phone gives one short buzz.
   * iOS Safari has no Vibration API, so there it is the press alone.
   *
   * Only fires on a change that was accepted: a refusal already has a toast,
   * and a buzz for something that did not happen would be a lie.
   */
  #pulse(card) {
    if (!card) return;

    card.setAttribute('data-tapped', '');
    clearTimeout(this.#tapTimers.get(card));
    this.#tapTimers.set(
      card,
      setTimeout(() => card.removeAttribute('data-tapped'), 260)
    );

    try {
      navigator.vibrate?.(10);
    } catch {
      /* some browsers throw when the page is not visible */
    }
  }

  #setStage(stage, scroll = false) {
    if (!STAGES.includes(stage)) return;
    this.#stage = stage;
    this.dataset.stage = stage;

    for (const tab of this.querySelectorAll('[data-lfb-stage-tab]')) {
      tab.setAttribute('aria-selected', String(tab.dataset.lfbStageTab === stage));
    }
    for (const panel of this.querySelectorAll('[data-lfb-panel]')) {
      panel.hidden = panel.dataset.lfbPanel !== stage;
    }

    const search = this.querySelector('[data-lfb-search]');
    if (search) {
      search.value = '';
      this.#query = '';
    }
    this.#filter();
    this.#renderProgress();

    if (scroll) {
      const tabs = this.querySelector('.lfb-stages');
      tabs?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /* ================= rendering ================= */

  #render() {
    this.#renderCards();
    this.#renderSlots();
    this.#renderCounts();
    this.#renderProgress();
    this.#renderButtons();

    // Complete: fetch the live stock now, so Add to cart doesn't wait for it.
    if (this.#isComplete()) for (const handle of this.#selectedHandles()) this.#liveFor(handle);
  }

  #renderCards() {
    for (const stage of STAGES) {
      const locked = this.#remaining(stage) === 0;
      this.toggleAttribute(`data-${stage}-locked`, locked);

      for (const card of this.querySelectorAll(`[data-lfb-card][data-stage="${stage}"]`)) {
        const qty = this.#sel[stage].get(card.dataset.variantId) || 0;
        card.classList.toggle('is-selected', qty > 0);

        // The badge is only worth drawing once a fragrance is in twice.
        card.toggleAttribute('data-multiple', qty > 1);

        const badge = card.querySelector('[data-lfb-qty-badge]');
        if (badge) badge.textContent = String(qty);

        const select = card.querySelector('[data-lfb-select]');
        if (select && card.dataset.soldout !== 'true') {
          const labels = this.#config.selectLabels || {};
          if (qty > 1) select.textContent = (labels.many || 'Selected ×[n]').replace('[n]', String(qty));
          else if (qty === 1) select.textContent = labels.one || 'Selected';
          else select.textContent = labels.none || 'Select';
        }
        const out = card.querySelector('[data-lfb-qty-out]');
        if (out) out.value = String(qty);

        const inc = card.querySelector('[data-lfb-inc]');
        if (inc) {
          inc.disabled = locked || !this.#config.allowDuplicates;
          // At the stock limit it looks disabled but still takes the tap, so
          // the customer is told why instead of meeting a dead button.
          const atStock = !inc.disabled && this.#pickedOf(card) >= this.#capOf(card);
          if (atStock) inc.setAttribute('aria-disabled', 'true');
          else inc.removeAttribute('aria-disabled');
        }
      }
    }
  }

  #renderSlots() {
    for (const stage of STAGES) {
      const limit = this.#limit(stage);
      const picks = [];
      for (const [id, qty] of this.#sel[stage]) {
        for (let i = 0; i < qty; i += 1) picks.push(id);
      }

      const slotWord = this.#config.slotLabels?.[stage] || 'Slot';

      const html = [];
      for (let i = 0; i < limit; i += 1) {
        const id = picks[i];
        const card = id ? this.#card(stage, id) : null;

        if (card) {
          const thumb = card.dataset.thumb;
          html.push(
            `<li class="lfb-slot is-filled">
               <span class="lfb-slot__box">
                 ${thumb ? `<img class="lfb-slot__img" src="${thumb}" alt="" loading="lazy" decoding="async">` : ''}
                 <button type="button" class="lfb-slot__x" data-lfb-slot-remove data-stage="${stage}" data-variant-id="${id}"
                         aria-label="Remove ${this.#escape(card.dataset.title)} from your bundle">
                   <svg viewBox="0 0 10 10" aria-hidden="true"><path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
                 </button>
               </span>
               <span class="lfb-slot__name">${this.#escape(card.dataset.title)}</span>
             </li>`
          );
        } else {
          const isNext = i === picks.length;
          html.push(
            `<li class="lfb-slot${isNext ? ' is-next' : ''}">
               <span class="lfb-slot__box"><span class="lfb-slot__plus" aria-hidden="true">+</span></span>
               <span class="lfb-slot__name">${this.#escape(slotWord)} ${i + 1}</span>
             </li>`
          );
        }
      }

      for (const list of this.querySelectorAll(`[data-lfb-slots="${stage}"]`)) {
        list.innerHTML = html.join('');
      }
    }
  }

  #renderCounts() {
    for (const stage of STAGES) {
      const chosen = this.#chosen(stage);
      const limit = this.#limit(stage);

      for (const el of this.querySelectorAll(`[data-lfb-tab-count="${stage}"]`)) {
        el.textContent = `${chosen} of ${limit} selected`;
      }
      for (const el of this.querySelectorAll(`[data-lfb-group-count="${stage}"]`)) {
        el.textContent = `${chosen}/${limit}`;
      }
      const tab = this.querySelector(`[data-lfb-stage-tab="${stage}"]`);
      tab?.classList.toggle('is-complete', chosen === limit);
    }

    const total = this.#chosen('full') + this.#chosen('sample');
    const totalLimit = this.#limit('full') + this.#limit('sample');
    for (const status of this.querySelectorAll('[data-lfb-bar-status]')) {
      status.textContent = `${total} of ${totalLimit} chosen — view bundle`;
    }
  }

  #renderProgress() {
    const total = this.#chosen('full') + this.#chosen('sample');
    const totalLimit = this.#limit('full') + this.#limit('sample');

    const percent = `${Math.round((total / totalLimit) * 100)}%`;
    for (const fill of this.querySelectorAll('[data-lfb-progress-fill]')) {
      fill.style.inlineSize = percent;
    }

    this.toggleAttribute('data-complete', this.#isComplete());

    const msg = this.querySelector('[data-lfb-progress-msg]');
    if (!msg) return;

    const stage = this.#stage;
    const left = this.#remaining(stage);
    const name = stage === 'full' ? this.#config.labels.full : this.#config.labels.sample;

    if (this.#isComplete()) {
      msg.innerHTML = `<strong>Bundle complete.</strong> Add it to your cart below.`;
    } else if (left === 0) {
      const other = stage === 'full' ? 'sample' : 'full';
      const otherName = other === 'full' ? this.#config.labels.full : this.#config.labels.sample;
      msg.innerHTML = `This stage is done — <span class="lfb-hl">${this.#remaining(other)} ${this.#escape(otherName)}</span> still to choose.`;
    } else {
      msg.innerHTML = `Choose <span class="lfb-hl">${left} more ${this.#escape(name)}${left === 1 ? '' : 's'}</span>.`;
    }
  }

  #renderButtons() {
    const complete = this.#isComplete();
    const outstanding = this.#remaining('full') + this.#remaining('sample');
    const label = complete
      ? this.#config.text.atcReady
      : String(this.#config.text.atcIncomplete).replace('[n]', String(outstanding));

    for (const button of this.querySelectorAll('[data-lfb-atc]')) {
      button.disabled = !complete || this.#submitting;
      if (!button.hasAttribute('data-busy')) button.textContent = label;
    }
  }

  /* ================= search ================= */

  #filter() {
    const grid = this.querySelector(`[data-lfb-grid="${this.#stage}"]`);
    if (!grid) return;

    const q = this.#query;
    let visible = 0;

    for (const card of grid.querySelectorAll('[data-lfb-card]')) {
      const hay = this.#normalize(
        [card.dataset.title, card.dataset.inspired, card.dataset.brand, card.dataset.family].join(' ')
      );
      const family = this.#normalize(card.dataset.family);
      const match =
        (!q || hay.includes(q)) && (!this.#family || family === this.#normalize(this.#family));
      card.toggleAttribute('data-filtered-out', !match);
      if (match) visible += 1;
    }

    this.toggleAttribute('data-empty', visible === 0);
  }

  #normalize(value) {
    return String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  #escape(value) {
    return String(value || '').replace(/[&<>"']/g, (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );
  }

  /* ================= persistence ================= */

  #storageKey() {
    return `lfb:${this.dataset.sectionId}:${this.#config.bundleVariantId}`;
  }

  #persist() {
    try {
      const payload = {
        full: Array.from(this.#sel.full.entries()),
        sample: Array.from(this.#sel.sample.entries()),
      };
      sessionStorage.setItem(this.#storageKey(), JSON.stringify(payload));
    } catch {
      /* private mode — selections simply won't survive a reload */
    }
  }

  #restore() {
    let payload = null;

    // ?lfb50= / ?lfb5= win, so the _edit link on an order rehydrates the
    // exact bundle. Plain comma-separated variant ids: URL-safe, readable
    // in the admin, and debuggable by eye.
    const params = new URLSearchParams(location.search);
    if (params.has('lfb50') || params.has('lfb5')) {
      payload = {
        full: this.#tally(params.get('lfb50')),
        sample: this.#tally(params.get('lfb5')),
      };
    }

    if (!payload) {
      try {
        payload = JSON.parse(sessionStorage.getItem(this.#storageKey()) || 'null');
      } catch {
        payload = null;
      }
    }
    if (!payload) return;

    for (const stage of STAGES) {
      for (const [id, qty] of payload[stage] || []) {
        const card = this.#card(stage, String(id));
        // Silently drop anything that has since sold out or left the collection.
        if (!card || card.dataset.soldout === 'true') continue;
        const capped = Math.min(Number(qty) || 0, Number(card.dataset.cap || 99));
        if (capped > 0) this.#sel[stage].set(String(id), capped);
      }
      // Trim if the merchant has since reduced the required count.
      while (this.#chosen(stage) > this.#limit(stage)) {
        const lastId = Array.from(this.#sel[stage].keys()).pop();
        const q = this.#sel[stage].get(lastId);
        if (q > 1) this.#sel[stage].set(lastId, q - 1);
        else this.#sel[stage].delete(lastId);
      }
    }

    const unfinished = STAGES.find((stage) => this.#remaining(stage) > 0);
    if (unfinished && unfinished !== this.#stage) this.#setStage(unfinished);
  }

  /**
   * "123,456,123" → [["123",2],["456",1]] — a repeated id in the link
   * means the customer picked that fragrance twice.
   */
  #tally(csv) {
    const counts = new Map();
    for (const raw of String(csv || '').split(',')) {
      const id = raw.trim();
      if (!/^\d+$/.test(id)) continue;
      counts.set(id, (counts.get(id) || 0) + 1);
    }
    return Array.from(counts.entries());
  }

  /* ================= validation ================= */

  /**
   * The last word before adding. Returns the cards that sold out since the
   * page loaded (live product JSON) and the cards that would take more than
   * is left once the cart's own units are counted. A network failure is not
   * treated as a sell-out: blocking a good customer on a flaky request would
   * cost more than it saves.
   *
   * Both inputs are normally already in hand — the cart is read on load and
   * after every change, and the product JSON is prefetched the moment the
   * bundle is complete — so this rarely waits on the network at all.
   */
  async #validate() {
    const [, lives] = await Promise.all([
      this.#ensureReserved(),
      Promise.all(this.#selectedHandles().map((handle) => this.#liveFor(handle))),
    ]);

    const byVariant = new Map();
    for (const data of lives) {
      for (const variant of data?.variants || []) byVariant.set(String(variant.id), variant);
    }

    const soldOut = [];
    const overStock = [];
    for (const stage of STAGES) {
      for (const id of this.#sel[stage].keys()) {
        const card = this.#card(stage, id);
        if (!card) continue;
        const live = byVariant.get(String(id));
        if (live && !live.available) soldOut.push(card);
        else if (this.#pickedOf(card) > this.#capOf(card)) overStock.push(card);
      }
    }
    return { soldOut, overStock };
  }

  #flagSoldOut(cards) {
    for (const card of cards) {
      card.dataset.soldout = 'true';
      card.setAttribute('aria-disabled', 'true');
      const stock = card.querySelector('[data-lfb-stock]');
      if (stock) {
        stock.innerHTML = `<span class="lfb-card__dot lfb-card__dot--out" aria-hidden="true"></span>Just sold out`;
      }
      const stage = card.dataset.stage;
      this.#sel[stage].delete(card.dataset.variantId);
    }
    cards[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.#persist();
    this.#render();
  }

  /* ================= add to cart ================= */

  async #addToCart() {
    if (this.#submitting || !this.#isComplete()) return;

    this.#submitting = true;
    const buttons = Array.from(this.querySelectorAll('[data-lfb-atc]'));
    buttons.forEach((b) => {
      b.disabled = true;
      b.setAttribute('data-busy', '');
    });

    try {
      const { soldOut, overStock } = await this.#validate();
      if (soldOut.length) {
        this.#toast(this.#config.text.soldOutError, true);
        this.#flagSoldOut(soldOut);
        return;
      }
      if (overStock.length) {
        this.#toast(this.#stockMessage(overStock[0]), true);
        if (overStock[0].dataset.stage !== this.#stage) this.#setStage(overStock[0].dataset.stage);
        overStock[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const bundleId = `LFB-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`.toUpperCase();
      const properties = this.#buildProperties(bundleId);

      const item = {
        id: Number(this.#config.bundleVariantId),
        quantity: 1,
        properties,
      };

      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);
      // The cart drawer's markup comes back with the add itself, so the drawer
      // paints from this response instead of asking the server a second time.
      if (document.querySelector('[data-lcd-root]')) sectionIds.push('cart-drawer-section');

      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'larcin-fixed-bundle',
          lines: [{ merchandiseId: String(item.id), quantity: 1 }],
          promise: deferred.promise,
        })
      );

      const cartAddUrl =
        (typeof Theme !== 'undefined' && Theme?.routes?.cart_add_url) || '/cart/add.js';

      const res = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [item],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();

      // The theme's listeners want the whole cart, which the add response
      // doesn't carry. It is read after the customer has been answered, not
      // before: the drawer already has its markup from the response above,
      // and nothing on screen should wait for a second request. The same read
      // refreshes the stock this builder counts against.
      const settle = (didError, itemCount, sections) => {
        const seq = ++this.#reservedSeq;
        this.#fetchCart()
          .then((cart) => {
            if (seq === this.#reservedSeq) this.#applyCart(cart);
            deferred.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
              detail: {
                didError,
                items: cart.items,
                source: 'larcin-fixed-bundle',
                sourceId: bundleId,
                itemCount,
                ...(sections ? { sections } : {}),
              },
            });
          })
          .catch((error) => console.error('[larcin-fixed-bundle] cart read failed', error));
      };

      if (!res.ok || data.status) {
        const message = data.description || data.message || this.#config.text.cartError;
        this.#toast(message, true);
        settle(true, 0);
        return;
      }

      // Counted straight away, so a second identical bundle can't slip in
      // before the cart read above comes back.
      for (const part of String(properties._inv || '').split('|')) {
        const match = /^(\d+)x(\d+)$/.exec(part.trim());
        if (match) this.#reserved.set(match[1], (this.#reserved.get(match[1]) || 0) + Number(match[2]));
      }
      settle(false, 1, data.sections);

      this.#toast(this.#config.text.added);
      this.#sel.full.clear();
      this.#sel.sample.clear();
      try {
        sessionStorage.removeItem(this.#storageKey());
      } catch {
        /* noop */
      }
      this.#setStage('full');
    } catch (error) {
      console.error('[larcin-fixed-bundle] cart error', error);
      this.#toast(this.#config.text.cartError, true);
    } finally {
      this.#submitting = false;
      buttons.forEach((b) => b.removeAttribute('data-busy'));
      this.querySelector('[data-lfb-sheet]')?.close();
      this.#render();
    }
  }

  /**
   * Two customer-visible properties carrying readable names, and three
   * short machine ones. Everything is kept compact deliberately: Shopify's
   * ADMIN order page renders underscore properties in full and offers no
   * way to hide them, so the only lever on admin clutter is making the
   * values small.
   *
   *   _bundle  type / unique id, one line
   *   _inv     inventory item ids with qty — the only thing Flow reads
   *   _edit    rebuild link, plain variant ids, URL-safe
   */
  #buildProperties(bundleId) {
    const names = { full: [], sample: [] };
    const ids = { full: [], sample: [] };
    const inv = [];
    let missingInv = 0;

    for (const stage of STAGES) {
      for (const [id, qty] of this.#sel[stage]) {
        const card = this.#card(stage, id);
        if (!card) continue;

        for (let i = 0; i < qty; i += 1) {
          names[stage].push(card.dataset.title);
          ids[stage].push(id);
        }

        const invId = (card.dataset.invId || '').trim();
        if (invId) inv.push(`${invId}x${qty}`);
        else missingInv += 1;
      }
    }

    const editUrl =
      `/products/${this.#config.bundleHandle}` +
      `?lfb50=${ids.full.join(',')}&lfb5=${ids.sample.join(',')}`;

    const props = {
      [this.#config.labels.fullProperty]: names.full.join(', '),
      [this.#config.labels.sampleProperty]: names.sample.join(', '),
      _bundle: `${this.#config.bundleType} / ${bundleId}`,
      _inv: inv.join('|'),
      _edit: editUrl,
    };

    // Surfaced only when something is wrong, so it never adds noise to a
    // healthy order. If this appears, the metafield job hasn't covered
    // every variant and the stock Flow will under-adjust.
    if (missingInv > 0) props._inv_missing = String(missingInv);

    return props;
  }

  async #fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    return res.json();
  }

  /* ================= toast ================= */

  #toast(message, isError = false) {
    const toast = this.querySelector('[data-lfb-toast]');
    const text = this.querySelector('[data-lfb-toast-text]');
    if (!toast || !text) return;

    text.textContent = message;
    toast.classList.toggle('lfb-toast--error', Boolean(isError));
    toast.classList.add('is-on');
    clearTimeout(this.#toastTimer);
    this.#toastTimer = setTimeout(() => toast.classList.remove('is-on'), 2600);
  }
}

if (!customElements.get('larcin-fixed-bundle')) {
  customElements.define('larcin-fixed-bundle', LarcinFixedBundle);
}
